import React, { useEffect, useState } from "react";
import UserGaurd from "../../helper/AuthGaurd";
import ColorInit from "../../helper/ColorInit";
import ScrollToTop from "react-scroll-to-top";
import Preloader from "../../helper/Preloader";
import HeaderTwo from "../../components/HeaderTwo";
import Breadcrumb from "../../components/Breadcrumb";
import FooterTwo from "../../components/FooterTwo";
import BottomFooter from "../../components/BottomFooter";
import { handleError, formatPrice } from "../../utils/functions";
import axiosInstance from "../../config/axios.config";
import { Link, useParams } from "react-router-dom";
import ReviewModal from "../../components/ReviewModal";
import useCurrency from "../../hooks/useCurrency";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

const OrderDetail = () => {
  const { id } = useParams();
  const [currency] = useCurrency();
  const [reviewModal, setReviewModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState(null);
  const { t } = useTranslation();

  useEffect(() => {
    const getData = async () => {
      try {
        setIsLoading(true);
        const { data } = await axiosInstance.get(`/user/order/${id}`);
        if (!data.error) {
          setData(data.data);
        }
      } catch (error) {
        handleError(error);
      } finally {
        setIsLoading(false);
      }
    };
    getData();
  }, [id]);

  const handleReviewSubmit = async (data) => {
    try {
      const { data: res } = await axiosInstance.post(`/user/review`, {
        ...data,
        reviewFor: reviewModal,
        onModel: "Product",
      });
      if (!res.error) {
        toast.success(res.message);
      } else {
        toast.error(res.message);
      }
    } catch (error) {
      handleError(error);
    } finally {
      setReviewModal(false);
    }
  };

  if (isLoading) return <Preloader isLoading={isLoading} />;

  return (
    <UserGaurd>
      <ColorInit color={true} />
      <ScrollToTop smooth color="#FA6400" />
      <HeaderTwo category={true} />
      <Breadcrumb title={t("orderDetails")} />

      <div className="container order-detail">
        {/* Address Section */}
        <div className="card mb-20 mt-40">
          <div className="py-20 card-header align-align-items-center d-flex justify-content-between">
            <h5 className="mb-0">{t("orderDetails")}</h5>
            <Link to={"invoice"} className="btn btn-main gap-8 rounded-8 py-16 fw-normal">View Invoice</Link>
          </div>
          <div className="card-body">
            <p className="d-flex justify-content-between">
              <strong>{t("orderNumber")}</strong> {data?._id}
            </p>
          </div>
        </div>

        <div className="card mb-20">
          <h5 className="card-header py-20">{t("shippingInformation")}</h5>
          <div className="card-body">
            <p className="d-flex justify-content-between">
              <strong>{t("address")}:</strong> {data?.address.address}
            </p>
            <p className="d-flex justify-content-between">
              <strong>{t("city")}:</strong> {data?.address.city}
            </p>
            <p className="d-flex justify-content-between">
              <strong>{t("state")}:</strong> {data?.address.state}
            </p>
            <p className="d-flex justify-content-between">
              <strong>{t("country")}:</strong> {data?.address.country.name}
            </p>
            <p className="d-flex justify-content-between">
              <strong>{t("zipCode")}:</strong> {data?.address.postalCode}
            </p>
          </div>
        </div>

        {/* Items Section */}
        <div className="card mb-20">
          <h6 className="card-header py-20">{t("orderedItems")}</h6>
          <div className="card-body">
            <div class="table-responsive">
              <table className="table">
              <thead>
                <tr>
                  <th className="pb-5 fw-semibold">{t("product")}</th>
                  <th className="pb-5 fw-semibold">{t("color")}</th>
                  <th className="pb-5 fw-semibold">{t("size")}</th>
                  <th className="pb-5 fw-semibold">{t("quantity")}</th>
                  <th className="pb-5 fw-semibold">{t("price")}</th>
                  <th className="pb-5 fw-semibold">{t("status")}</th>
                  <th className="pb-5 fw-semibold">{t("actions")}</th>
                </tr>
              </thead>
              <tbody>
                {data?.items.map((item, index) => (
                  <tr key={index}>
                    <td className="py-5">
                    {item.product.name.length > 40 
                      ? `${item.product.name.substring(0, 40)}...` 
                      : item.product.name
                    }
                    </td>
                    <td className="py-5">{item.color || "N/A"}</td>
                    <td className="py-5">{item.size || "N/A"}</td>
                    <td className="py-5">{item.quantity}</td>
                    <td className="py-5">{formatPrice(item.price, currency)}</td>
                    <td className="py-5">
                      <span className={`badge text-xs bg-${item.status === "PENDING" ? "warning" : "success"}`}>
                        {item.status}
                      </span>
                    </td>
                    <td className="py-5">
                      {item.status === "DELIVERED" && (
                        <button className="btn btn-success" onClick={() => setReviewModal(item.product._id)}>
                          {t("writeReview")}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Order Summary */}
        <div className="card mb-20">
          <h6 className="card-header py-20">{t("orderSummary")}</h6>
          <div className="card-body">
            <p className="d-flex justify-content-between">
              <strong>{t("total")}:</strong> {formatPrice(data?.totalAmount, currency)}
            </p>
            <p className="d-flex justify-content-between">
              <strong>{t("shippingCharges")}:</strong>{formatPrice(data?.shipmentCharges, currency)}
            </p>
            <p className="d-flex justify-content-between">
              <strong>{t("paymentMethod")}:</strong> {data?.paymentMethod}
            </p>
            {data?.paymentMethod !== "COD" && (
              <p className="d-flex justify-content-between">
                <strong>{t("paymentStatus")}:</strong> {data?.paymentStatus}
              </p>
            )}
            <p className="d-flex justify-content-between">
              <strong>{t("additionalNotes")}:</strong> {data?.additional}
            </p>
          </div>
        </div>
      </div>

      <ReviewModal visible={!!reviewModal} setVisible={setReviewModal} onSubmit={handleReviewSubmit} />

      <FooterTwo />
      <BottomFooter />
    </UserGaurd>
  );
};

export default OrderDetail;
